import { Post as BasePost } from '@/lib/supabase/client';
import { Profile } from '@/lib/supabase/client';

// Post types enum
export enum PostType {
  DEEP_DIVE = 'deep-dive',
  BLOG = 'blog',
  TWITTER = 'twitter',
  NEWS = 'news'
}

// Relationship types enum
export enum RelationshipType {
  SERIES = 'series',
  RELATED = 'related',
  PREREQUISITE = 'prerequisite',
  CONTRASTING_VIEWPOINT = 'contrasting_viewpoint',
  FOLLOW_UP = 'follow_up'
}

// Technical level enum
export enum TechnicalLevel {
  BEGINNER = 'beginner',
  INTERMEDIATE = 'intermediate',
  ADVANCED = 'advanced'
}

// Content temperature enum
export enum ContentTemperature {
  EVERGREEN = 'evergreen',
  TIME_SENSITIVE = 'time-sensitive'
}

// Sentiment enum
export enum Sentiment {
  POSITIVE = 'positive',
  NEUTRAL = 'neutral',
  NEGATIVE = 'negative'
}

// Use case enum
export enum UseCase {
  EDUCATIONAL = 'educational',
  DECISION_SUPPORT = 'decision-support',
  NEWS = 'news',
  ANALYSIS = 'analysis'
}

// Post relationship interface
export interface PostRelationship {
  id: number;
  source_post_id: number;
  target_post_id: number;
  relationship_type: RelationshipType;
  order_in_series?: number;
  created_at: string;
  updated_at: string;
}

// Related post interface (from get_related_posts function)
export interface RelatedPost {
  related_post_id: number;
  relationship_type: RelationshipType;
  order_in_series?: number;
  title: string;
  post_type: PostType;
  author_id: string;
}

// Metadata interfaces for different aspects of post metadata
export interface AIReadyMetadata {
  key_takeaways: string[];
  content_relevance_score?: number;
  sentiment: Sentiment;
}

export interface ContentClassification {
  complexity_score: number; // 1-5
  technical_level: TechnicalLevel;
  content_temperature: ContentTemperature;
  primary_topic?: string;
  secondary_topics?: string[];
}

export interface TargetAudience {
  investor_types?: string[];
  knowledge_levels?: string[];
  interests?: string[];
  portfolio_relevance?: string[];
}

// All metadata combined
export interface PostMetadata {
  ai_ready?: AIReadyMetadata;
  classification?: ContentClassification;
  audience?: TargetAudience;
  tags?: string[];
  use_case?: UseCase;
  action_inspiration?: boolean;
}

// Extended Post type with all new fields from the database
export interface ExtendedPost extends BasePost {
  excerpt?: string;
  reading_time?: number;
  summary?: string;
  metadata?: PostMetadata;
  complexity_score?: number;
  technical_level?: TechnicalLevel;
  content_temperature?: ContentTemperature;
  target_audience?: string[];
  confidence_score?: number;
  key_takeaways?: string[];
  sentiment?: Sentiment;
  relationships?: PostRelationship[];
}

// Post with author profile data
export interface PostWithProfile extends ExtendedPost {
  profiles?: Pick<Profile, 'username' | 'display_name' | 'avatar_url'>;
}

// Section for Deep Dive posts
export interface PostSection {
  id?: string; // Client-side ID for new sections
  title: string;
  content: string;
  display_order: number;
}

// Deep Dive post specific data
export interface DeepDivePost extends ExtendedPost {
  sections?: PostSection[];
}

// Blog post specific data
export interface BlogPost extends ExtendedPost {
  // Blog posts use the standard ExtendedPost fields
}

// Twitter post specific data
export interface TwitterPost extends ExtendedPost {
  // Twitter posts use the standard ExtendedPost fields
}

// News post specific data
export interface NewsPost extends ExtendedPost {
  source?: string;
  impact_level?: 'high' | 'medium' | 'low';
}

// Post creation/update input type
export interface PostInput {
  title: string;
  content: string;
  post_type: PostType;
  excerpt?: string;
  summary?: string;
  complexity_score?: number;
  technical_level?: TechnicalLevel;
  content_temperature?: ContentTemperature;
  target_audience?: string[];
  confidence_score?: number;
  key_takeaways?: string[];
  sentiment?: Sentiment;
  metadata?: PostMetadata;
  is_published?: boolean;
} 